import { AllowFunc } from '@fullcalendar/core';
import { addDays, isSameDay, subMilliseconds } from 'date-fns';
import { WeekDays } from 'shared/types/week-days';
import { getTaskPlanning } from 'shared/utils/get-task-planning';
import { getScheduleByDate } from 'shared/utils/schedules';

export const eventAllowBase: AllowFunc = (dropInfo, draggedEvent) => {
  if (!draggedEvent) {
    return false;
  }

  // make sure a deadline event cannot be moved out of the all-day section
  if (draggedEvent.extendedProps.isDeadline && !dropInfo.allDay) {
    return false;
  }

  // The single-day rule is a task/deadline rule: a task lives on one day by construction.
  // External calendar events legitimately span days (a multi-day conference, an overnight
  // flight) and must keep their span when moved.
  if (draggedEvent.extendedProps.externalId) {
    return true;
  }

  // make sure an event cannot cover multiple days
  return isSameDay(dropInfo.start, subMilliseconds(dropInfo.end, 1));
};

export const eventAllowHabit: AllowFunc = (dropInfo, draggedEvent) => {
  // basic checks on the dragged event
  if (!draggedEvent) {
    return false;
  }

  // if not a habit or repeating task, this check is not needed
  if (!draggedEvent.extendedProps.task?.schedules?.length) {
    return true;
  }

  // A side-panel (external) drag has no committed `start`. Dropping it sets the
  // drop day's own occurrence (a reschedule, never a duplicate of another day's
  // occurrence), so allow it on any day — including one that already has an
  // occurrence. The one-per-date guard below only applies to relocating an
  // event already placed on the calendar (which carries its `start`).
  if (!draggedEvent.start) {
    return true;
  }

  // make sure we have all necessary properties
  if (!draggedEvent.extendedProps.weekStartsOn) {
    return false;
  }

  // get the active schedule for the drop date to have the correct calculations
  const today = new Date();
  const schedule = getScheduleByDate(
    draggedEvent.extendedProps.task?.schedules,
    {
      date: draggedEvent.start ?? dropInfo.start,
      today,
    },
  );
  // no schedule means no plan, so we can allow the drop
  if (!schedule) {
    return true;
  }

  // get the date range of the current displayed range by fullcalendar
  // @ts-ignore - this is a private property, but we need it to get the correct dates
  const { start, end } = draggedEvent._context.calendarApi.currentData
    ?.dateProfile?.currentRange ?? {
    start: addDays(dropInfo.start, -16),
    end: addDays(dropInfo.start, 16),
  };

  const weekStartsOn = draggedEvent.extendedProps.weekStartsOn as WeekDays;

  // get the planning for the task
  const planning = draggedEvent.extendedProps.task
    ? getTaskPlanning(draggedEvent.extendedProps.task, {
        startDate: start,
        endDate: end,
        today: new Date(),
        weekStartsOn,
        strictEndDate: true,
      })
    : [];

  return (
    // ensure the date where the event started is allowed
    (draggedEvent.start && isSameDay(dropInfo.start, draggedEvent.start)) ||
    (draggedEvent.extendedProps.originDate &&
      isSameDay(dropInfo.start, draggedEvent.extendedProps.originDate)) ||
    // ensure other planned dates are not allowed; only one event per date
    !planning.find(({ date }) => isSameDay(date, dropInfo.start))
  );
};

export const eventAllow: AllowFunc = (dropInfo, draggedEvent) =>
  eventAllowBase(dropInfo, draggedEvent) &&
  eventAllowHabit(dropInfo, draggedEvent);
